/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo } from 'react';

import debouncePromise from 'debounce-promise';

import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Flex } from '@atlaskit/primitives/compiled';
import { type GroupBase } from '@atlaskit/react-select';
import Select, { type OptionType, type SelectProps } from '@atlaskit/select';

import { OptionIcon } from '../option-icon';

export type DebouncedSelectorProps<T> = {
	loadOptions: (query: string) => Promise<T[] | GroupBase<T>[]>;
} & SelectProps<T, false>;

const DEBOUNCE_DELAY = 300;

const styles = cssMap({
	optionLabel: {
		overflow: 'hidden',
		textOverflow: 'wrap',
		width: '100%',
	},
});

export const DebouncedSelector = <T extends OptionType>({
	loadOptions,
	...props
}: DebouncedSelectorProps<T>): JSX.Element => {
	const debouncedLoadOptions = useMemo(() => {
		return debouncePromise(loadOptions, DEBOUNCE_DELAY);
	}, [loadOptions]);

	const handleLoadOptions = useCallback(
		async (searchText: string) => {
			// Show debounced search results for what they've typed into the selector.
			return await debouncedLoadOptions(searchText);
		},
		[debouncedLoadOptions],
	);

	const formatOptionLabel = useCallback((option: T) => {
		return (
			<Flex alignItems="center" justifyContent="start" gap="space.100" xcss={styles.optionLabel}>
				<OptionIcon type={option.type} subType={option.subType} iconPath={option.iconPath} />
				{option.label}
			</Flex>
		);
	}, []);

	const formatGroupLabel = useCallback((group: GroupBase<T>) => {
		return <Heading size="xxsmall">{group.label || ''}</Heading>;
	}, []);

	return (
		<Select
			spacing="default"
			appearance="default"
			cacheOptions
			defaultOptions
			loadOptions={handleLoadOptions}
			formatOptionLabel={formatOptionLabel}
			formatGroupLabel={formatGroupLabel}
			{...props}
		/>
	);
};
